import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Flame, History, ShieldAlert, TrendingDown, UserCog } from "lucide-react";
import { rpc } from "../../lib/rpc";
import { movementTypeLabel } from "../../lib/movementTypes";
import { AdminPageHeader } from "../../components/admin/DataTable";

// #136 Befund 1: jede Kachel ist eine Arbeitsliste -- Ziel mit passendem
// Vorfilter statt ungefilterter Seite.
const TILES = [
    { key: "total_articles", label: "Artikel", className: "text-secondary", href: "/admin/artikel" },
    { key: "open_loans", label: "Ausleihen", className: "text-info", href: "/admin/ausleihen" },
    { key: "overdue_loans", label: "Überfällig", className: "text-warning", href: "/admin/ausleihen?overdue=1" },
    { key: "low_stock", label: "Niedrig", className: "text-warning", href: "/admin/artikel?filter=low" },
    { key: "critical_stock", label: "Kritisch", className: "text-destructive", href: "/admin/artikel?filter=under-min" },
    { key: "open_restock", label: "Nachbest.", className: "text-info", href: "/admin/nachbestellung" },
    { key: "staging_positions", label: "Staging", className: "text-primary", href: "/admin/datenqualitaet" },
];

export default function AdminUebersicht() {
    const navigate = useNavigate();
    const [kpis, setKpis] = useState(null);
    const [movements, setMovements] = useState([]);
    const [unknowns, setUnknowns] = useState(0);

    useEffect(() => {
        Promise.all([rpc.get("/dashboard/kpis"), rpc.get("/movements?limit=50")]).then(
            ([k, m]) => {
                if (k.ok) setKpis(k.data);
                if (m.ok) setMovements(m.data || []);
            }
        );
    }, []);

    const todoList = [];
    if (kpis) {
        if (kpis.staging_positions > 0)
            todoList.push({
                icon: Flame,
                tone: "text-warning",
                label: `${kpis.staging_positions} Staging-Position(en) nicht final eingelagert`,
                href: "/admin/datenqualitaet",
            });
        if (kpis.critical_stock > 0)
            todoList.push({
                icon: ShieldAlert,
                tone: "text-destructive",
                label: `${kpis.critical_stock} kritische Artikel unter Mindestbestand`,
                href: "/admin/artikel?filter=under-min",
            });
        if (kpis.overdue_loans > 0)
            todoList.push({
                icon: UserCog,
                tone: "text-warning",
                label: `${kpis.overdue_loans} überfällige Ausleihe(n)`,
                href: "/admin/ausleihen?overdue=1",
            });
        if (kpis.open_restock > 0)
            todoList.push({
                icon: TrendingDown,
                tone: "text-info",
                label: `${kpis.open_restock} offene Nachbestellungen`,
                href: "/admin/nachbestellung",
            });
    }

    return (
        <>
            <AdminPageHeader
                title="Leitstand"
                subtitle="Handlungsbedarf, KPIs und letzte Aktivität"
            />

            <div className="mb-8 grid grid-cols-3 gap-3 lg:grid-cols-7" data-testid="admin-kpi-band">
                {TILES.map((t) => {
                    const Tag = t.href ? "button" : "div";
                    return (
                        <Tag
                            key={t.key}
                            {...(t.href
                                ? { type: "button", onClick: () => navigate(t.href) }
                                : {})}
                            className={`rounded-xl border border-border bg-card/60 p-4 text-left ${t.href ? "transition-colors hover:border-primary/40" : ""}`}
                            data-testid={`admin-kpi-${t.key}`}
                        >
                            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                {t.label}
                            </div>
                            <div className={`mt-1 text-3xl font-black ${t.className}`}>
                                {kpis?.[t.key] ?? "—"}
                            </div>
                        </Tag>
                    );
                })}
            </div>

            <div className="grid grid-cols-12 gap-6">
                <section className="col-span-12 lg:col-span-5">
                    <h2 className="mb-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        Zu erledigen
                    </h2>
                    <div className="flex flex-col gap-2" data-testid="admin-todo-list">
                        {todoList.length === 0 ? (
                            <div className="rounded-md border border-border/60 bg-card/40 px-3 py-4 text-xs text-muted-foreground">
                                Alles im Griff. 
                            </div>
                        ) : (
                            todoList.map((t, i) => {
                                const Icon = t.icon;
                                return (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => navigate(t.href)}
                                        className="flex items-center gap-3 rounded-md border border-border bg-card/40 px-3 py-2 text-left text-sm text-foreground hover:border-primary/50"
                                        data-testid={`admin-todo-${i}`}
                                    >
                                        <Icon className={`h-4 w-4 ${t.tone}`} />
                                        <span>{t.label}</span>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </section>
                <section className="col-span-12 lg:col-span-7">
                    <h2 className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        <History className="h-3 w-3" /> Aktuelle Aktivität
                    </h2>
                    <div className="overflow-x-auto rounded-lg border border-border bg-card/40">
                        <table className="w-full text-sm">
                            <thead className="bg-card/60 text-xs uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="px-3 py-2 text-left">Typ</th>
                                    <th className="px-3 py-2 text-left">Menge</th>
                                    <th className="px-3 py-2 text-left">Kommentar</th>
                                    <th className="px-3 py-2 text-left">Zeit</th>
                                </tr>
                            </thead>
                            <tbody>
                                {movements.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-3 py-6 text-center text-xs text-muted-foreground">
                                            Noch keine Bewegungen.
                                        </td>
                                    </tr>
                                ) : (
                                    movements.map((m) => (
                                        <tr key={m.id} className="border-t border-border/60">
                                            <td className="px-3 py-2 uppercase text-primary text-xs">
                                                {movementTypeLabel(m.type)}
                                            </td>
                                            <td className="px-3 py-2">{m.quantity ?? "—"}</td>
                                            <td className="px-3 py-2 text-muted-foreground">{m.comment || "—"}</td>
                                            <td className="px-3 py-2 text-muted-foreground">
                                                {new Date(m.created_at).toLocaleString("de-DE")}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </>
    );
}
